<template>
  <section class="upscale-workspace">
    <div class="upscale-grid">
      <aside class="upscale-controls">
        <div class="control-heading">
          <span class="control-icon"><i class="el-icon-full-screen"></i></span>
          <div><strong>高清放大</strong><small>提升清晰度，同时保持原图构图与细节</small></div>
        </div>

        <label class="upscale-upload" :class="{ filled: previewUrl, dragging }"
          @dragenter.prevent="dragging = true" @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false" @drop.prevent="handleDrop">
          <input type="file" accept="image/png,image/jpeg,image/webp" @change="handleFileInput" />
          <template v-if="previewUrl">
            <img :src="previewUrl" alt="待放大的原始图片" />
            <span><i class="el-icon-refresh"></i> 更换图片</span>
          </template>
          <template v-else>
            <i class="el-icon-upload2"></i>
            <strong>上传需要放大的图片</strong>
            <small>点击选择或拖到这里，支持 JPG / PNG / WebP，最大 10MB</small>
          </template>
        </label>

        <div v-if="sourceWidth" class="source-meta">
          <span><small>原始尺寸</small><strong>{{ sourceWidth }} × {{ sourceHeight }}</strong></span>
          <i class="el-icon-right"></i>
          <span><small>预计输出</small><strong>{{ outputDimensions }}</strong></span>
        </div>

        <div class="setting-block">
          <label>输出清晰度</label>
          <div class="option-grid two">
            <button v-for="item in resolutions" :key="item.value" type="button"
              :class="{ active: resolution === item.value }" :disabled="generating"
              @click="resolution = item.value">
              <strong>{{ item.label }}</strong><small>{{ item.hint }}</small>
            </button>
          </div>
        </div>

        <div class="setting-block">
          <label>细节增强</label>
          <div class="option-grid three">
            <button v-for="item in strengths" :key="item.value" type="button"
              :class="{ active: strength === item.value }" :disabled="generating"
              @click="strength = item.value">
              <strong>{{ item.label }}</strong><small>{{ item.hint }}</small>
            </button>
          </div>
        </div>

        <div class="setting-block">
          <label>生成质量</label>
          <QualitySelector v-model="quality" :disabled="generating" :show-label="false" />
        </div>

        <div class="preserve-note">
          <i class="el-icon-lock"></i>
          <span><strong>原图保护</strong><small>不改变构图、颜色、材质、文字与物体形态</small></span>
        </div>

        <button class="upscale-generate" type="button" :disabled="!file || generating" @click="generate">
          <i :class="generating ? 'el-icon-loading' : 'el-icon-magic-stick'"></i>
          <span><strong>{{ generating ? generationText : '开始高清放大' }}</strong><small>{{ generating ? `${progress}%` : `预计消耗 ${selectedQualityPoints} 积分` }}</small></span>
        </button>
        <div v-if="generating" class="upscale-progress"><i :style="{ width: `${progress}%` }"></i></div>
        <div v-if="errorMessage" class="upscale-error"><i class="el-icon-warning-outline"></i><span>{{ errorMessage }}</span></div>
      </aside>

      <main class="upscale-preview">
        <header>
          <div><strong>效果预览</strong><small>{{ resultUrl ? '高清结果已生成，可点击查看大图' : '上传图片后在这里查看处理效果' }}</small></div>
          <div v-if="resultUrl" class="preview-actions">
            <button type="button" @click="previewResult"><i class="el-icon-zoom-in"></i> 查看大图</button>
            <WorkflowTuningButton
              variant="button"
              :image-url="resultUrl"
              :prompt="prompt"
              source-model="gpt-image-2-official"
              module-name="高清放大"
            />
            <button type="button" class="primary" @click="downloadResult"><i class="el-icon-download"></i> 下载高清图</button>
          </div>
        </header>
        <div v-if="!previewUrl" class="preview-empty">
          <span><i class="el-icon-picture-outline"></i></span>
          <strong>等待上传图片</strong>
          <small>建议上传画面完整、主体清晰的原图</small>
        </div>
        <div v-else class="comparison">
          <button type="button" @click="previewSource">
            <img :src="previewUrl" alt="高清放大前的原图" />
            <span>原图 · {{ sourceWidth }} × {{ sourceHeight }}</span>
          </button>
          <button v-if="resultUrl" type="button" @click="previewResult">
            <img :src="$ossImageUrl(resultUrl, 'preview')" alt="高清放大后的结果" />
            <span class="result-label">高清结果 · {{ resolution }}</span>
          </button>
          <div v-else class="result-placeholder">
            <i :class="generating ? 'el-icon-loading' : 'el-icon-full-screen'"></i>
            <strong>{{ generating ? generationText : '高清结果' }}</strong>
            <small>{{ generating ? 'AI 正在重建纹理与边缘细节' : '完成设置后点击“开始高清放大”' }}</small>
          </div>
        </div>
      </main>
    </div>

    <section class="upscale-history">
      <header>
        <div><strong>创作历史</strong><small>高清放大任务会自动保存在这里</small></div>
        <button :disabled="historyLoading" title="刷新创作历史" @click="loadHistory"><i :class="historyLoading ? 'el-icon-loading' : 'el-icon-refresh'"></i></button>
      </header>
      <div v-if="history.length" class="history-grid">
        <article v-for="record in history" :key="record.id" class="history-card">
          <button class="history-card-preview" type="button" :disabled="!record.result" @click="previewRecord(record)">
            <img v-if="record.result" :src="$ossImageUrl(record.result, 'thumbnail')" alt="历史高清放大结果" loading="lazy" />
            <span v-else><i :class="record.status === 'failed' ? 'el-icon-warning-outline' : 'el-icon-loading'"></i></span>
            <ImageDimensionsRow :items="[{ label: '图片尺寸', url: record.result, width: record.resultWidth, height: record.resultHeight }]" />
            <small>{{ record.resolution || '高清放大' }} · {{ formatTime(record.createdAt) }}</small>
            <GenerationModelDetails :record="record" />
            <GenerationFailureDetails :record="record" />
          </button>
          <WorkflowTuningButton
            v-if="record.result"
            class="history-card-workflow"
            :image-url="record.result"
            :prompt="record.prompt || prompt"
            :source-model="record.model || ''"
            module-name="高清放大"
          />
        </article>
      </div>
      <div v-else class="history-empty">
        <span><i :class="historyLoading ? 'el-icon-loading' : 'el-icon-time'"></i></span>
        <strong>{{ historyLoading ? '正在加载创作历史' : '暂无高清放大记录' }}</strong>
        <small>{{ historyLoading ? '请稍候…' : '完成第一次高清放大后，结果会自动出现在这里' }}</small>
      </div>
      <AppPagination :page="historyPage" :page-size="12" :total="historyTotal" :loading="historyLoading" @change="changeHistoryPage" />
    </section>
  </section>
</template>

<script>
/* 高清放大只增强结果，不改变原生成批次状态；输入可能来自上传、OSS 或历史记录。 */
import { apiRequest, downloadResource } from '@/services/api'
import { closestImageRatio } from '@/utils/imageAspectRatio'
import ImageDimensionsRow from '@/components/ImageDimensionsRow.vue'
import QualitySelector from '@/components/QualitySelector.vue'
import { DEFAULT_GENERATION_QUALITY, generationPoints } from '@/utils/outputQuality'

export default {
  name: 'HighResolutionUpscale',
  components: { ImageDimensionsRow, QualitySelector },
  data() {
    return {
      file: null, previewUrl: '', resultUrl: '', dragging: false,
      sourceWidth: 0, sourceHeight: 0, resolution: '2K', quality: DEFAULT_GENERATION_QUALITY, strength: 'standard',
      generating: false, progress: 0, generationText: '准备处理', errorMessage: '',
      pollingStopped: false, requestControllers: [], history: [], historyLoading: false, historyPage: 1, historyTotal: 0,
      resolutions: [
        { value: '2K', label: '2K 高清', hint: '适合日常展示' },
        { value: '4K', label: '4K 超清', hint: '适合大屏与印刷' }
      ],
      strengths: [
        { value: 'soft', label: '柔和', hint: '自然降噪' },
        { value: 'standard', label: '标准', hint: '推荐' },
        { value: 'strong', label: '强力', hint: '强化纹理' }
      ]
    }
  },
  computed: {
    selectedQualityPoints() {
      return generationPoints(this.quality)
    },
    outputDimensions() {
      if (!this.sourceWidth) return '—'
      const longEdge = this.resolution === '4K' ? 4096 : 2048
      const scale = longEdge / Math.max(this.sourceWidth, this.sourceHeight)
      return `${Math.round(this.sourceWidth * scale)} × ${Math.round(this.sourceHeight * scale)}`
    },
    ratio() {
      return closestImageRatio(this.sourceWidth, this.sourceHeight)
    },
    prompt() {
      const detail = this.strength === 'soft' ? '轻柔降噪，细节自然克制' : this.strength === 'strong' ? '加强纹理、边缘与微小细节恢复' : '均衡提升清晰度、纹理和边缘细节'
      return `对输入图片进行专业高清无损放大，输出${this.resolution}图像。${detail}。严格保持原图构图、镜头视角、物体位置、形态比例、颜色、材质、光影、文字、Logo和全部内容不变；不得新增、删除、替换或重新设计任何元素；不得改变画幅，不得裁切。消除低分辨率锯齿、压缩噪点和模糊，恢复真实自然的边缘与材质纹理，避免过度锐化、塑料感、光晕、伪影和虚构细节。只输出一张高清增强后的完整图片。`
    }
  },
  created() { this.loadHistory() },
  beforeDestroy() {
    this.pollingStopped = true
    this.requestControllers.forEach(item => item.abort())
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
  },
  methods: {
    request(url, options = {}) {
      return apiRequest(url, options, { controllers: this.requestControllers, isStopped: () => this.pollingStopped })
    },
    handleFileInput(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      this.acceptFile(file)
    },
    handleDrop(event) {
      this.dragging = false
      this.acceptFile(event.dataTransfer.files && event.dataTransfer.files[0])
    },
    acceptFile(file) {
      if (!file) return
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return this.$message.warning('仅支持 JPG、PNG 或 WebP 图片')
      if (file.size >= 10 * 1024 * 1024) return this.$message.warning('图片必须小于 10MB，请压缩后再上传')
      const url = URL.createObjectURL(file)
      const image = new Image()
      image.onload = () => {
        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
        this.file = file
        this.previewUrl = url
        this.sourceWidth = image.naturalWidth
        this.sourceHeight = image.naturalHeight
        this.resultUrl = ''
        this.errorMessage = ''
      }
      image.onerror = () => { URL.revokeObjectURL(url); this.$message.error('图片无法读取，请重新选择') }
      image.src = url
    },
    taskImage(task) {
      const data = task && task.result && task.result.data
      return Array.isArray(data) && data[0] ? data[0].url : ''
    },
    recordImage(record) {
      const images = record && record.images
      if (!Array.isArray(images) || !images[0]) return ''
      return images[0].url || images[0]
    },
    async waitForAssignedTask(batchId) {
      const started = Date.now()
      while (Date.now() - started < 600000 && !this.pollingStopped) {
        const record = await this.request(`/api/records/${encodeURIComponent(batchId)}`, {
          timeoutMs: 15000,
          retries: 1,
        })
        const completedImage = this.recordImage(record)
        if (record.status === 'completed' && completedImage) return { image: completedImage }
        if (record.status === 'failed') {
          throw new Error(record.error || record.errorMessage || '高清放大任务提交失败')
        }
        const task = (record.tasks || []).find(item => item && item.id)
        if (task) return { taskId: task.id }
        this.progress = Math.max(this.progress, Math.min(16, Number(record.progress || 0)))
        this.generationText = '任务已创建，正在等待处理编号'
        await new Promise(resolve => window.setTimeout(resolve, 1500))
      }
      throw new Error('高清任务已创建，请稍后在创作历史中查看')
    },
    async pollTask(id) {
      const started = Date.now()
      while (Date.now() - started < 600000 && !this.pollingStopped) {
        const task = await this.request(`/api/generations/${encodeURIComponent(id)}`, { timeoutMs: 15000, retries: 1 })
        this.progress = Math.max(this.progress, Math.min(96, Number(task.progress || 0)))
        if (task.status === 'completed') {
          const image = this.taskImage(task)
          if (!image) throw new Error('任务已完成，但没有返回高清图片')
          return image
        }
        if (task.status === 'failed') throw new Error(task.error && task.error.message ? task.error.message : '高清放大失败')
        this.generationText = this.progress > 55 ? '正在恢复纹理细节' : '正在提升图片清晰度'
        await new Promise(resolve => window.setTimeout(resolve, 3000))
      }
      throw new Error('处理时间较长，请稍后在记录中查看')
    },
    async generate() {
      if (!this.file || this.generating) return
      this.generating = true
      this.progress = 4
      this.errorMessage = ''
      this.resultUrl = ''
      try {
        this.generationText = '正在上传原图'
        const form = new FormData()
        form.append('file', this.file)
        const asset = await this.request('/api/images/upload', { method: 'POST', body: form, timeoutMs: 120000 })
        this.progress = 12
        this.generationText = '正在创建高清任务'
        const random = window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
        const requestId = `upscale-${random}`.slice(0, 64)
        const batch = await this.request('/api/generations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrls: [asset.url], prompt: this.prompt, space: '图片高清放大',
            style: `${this.resolution} · ${this.strength}`, size: this.ratio,
            resolution: this.resolution, quality: this.quality, count: 1, stage: 'high_resolution',
            floorPlanRecordUrls: asset.localUrl ? [asset.localUrl] : [],
            referenceRecordUrls: [], requestId
          }),
          timeoutMs: 120000
        })
        const task = (batch.tasks || []).find(item => item && item.id)
        const batchId = batch.batchId || `interior-${requestId}`
        this.loadHistory()
        const assignment = task ? { taskId: task.id } : await this.waitForAssignedTask(batchId)
        this.progress = 18
        this.resultUrl = assignment.image || await this.pollTask(assignment.taskId)
        this.progress = 100
        this.generationText = '高清放大完成'
        this.$message.success('高清图片已生成')
        this.loadHistory()
      } catch (error) {
        this.errorMessage = error.message || '高清放大失败，请稍后重试'
        this.$message.error(this.errorMessage)
        this.loadHistory()
      } finally {
        this.generating = false
      }
    },
    async loadHistory() {
      if (this.historyLoading) return
      this.historyLoading = true
      try {
        const response = await this.request(`/api/records/paged?stage=high_resolution&page=${this.historyPage}&pageSize=12`)
        this.historyTotal = Number(response.total || 0)
        const unique = response.items || []
        this.history = unique.map(record => ({
          id: record.id, createdAt: record.createdAt, resolution: record.style, status: record.status,
          result: record.images && record.images[0] ? (record.images[0].url || record.images[0]) : '',
          resultWidth: record.images && record.images[0] ? record.images[0].width : null,
          resultHeight: record.images && record.images[0] ? record.images[0].height : null,
          taskStates: record.tasks || [], errorMessage: record.error || record.errorMessage || '', imageModel: record.imageModel || ''
        }))
      } catch (error) {
        this.history = []
      } finally {
        this.historyLoading = false
      }
    },
    changeHistoryPage(page) { this.historyPage = page; this.loadHistory() },
    previewSource() { this.$emit('preview-image', this.previewUrl, { title: '高清放大 · 原图' }) },
    previewResult() { this.$emit('preview-image', this.resultUrl, { title: `高清放大 · ${this.resolution} 结果` }) },
    previewRecord(record) { if (record.result) this.$emit('preview-image', record.result, { title: '历史高清放大结果' }) },
    async downloadResult() {
      try {
        const blob = await downloadResource(this.resultUrl)
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `高清放大-${this.resolution}-${Date.now()}.png`
        link.click()
        window.setTimeout(() => URL.revokeObjectURL(link.href), 1000)
      } catch (error) { this.$message.error(error.message || '下载失败') }
    },
    formatTime(value) {
      if (!value) return ''
      return new Intl.DateTimeFormat('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(value))
    }
  }
}
</script>

<style scoped>
.upscale-workspace{width:calc(100% - 68px);max-width:none;margin:26px auto 64px;padding:0;color:#243029}.upscale-grid{display:grid;grid-template-columns:360px minmax(0,1fr);gap:16px}.upscale-controls,.upscale-preview,.upscale-history{border:1px solid #e5eae6;border-radius:10px;background:#fff;box-shadow:0 6px 22px rgba(29,43,35,.045)}.upscale-controls{padding:20px}.control-heading{display:flex;align-items:center;gap:12px;margin-bottom:17px}.control-icon{width:38px;height:38px;display:grid;place-items:center;border-radius:8px;background:#edf3ff;color:#2f63cf;font-size:18px}.control-heading>div{display:flex;flex-direction:column;gap:3px}.control-heading strong{font-size:16px}.control-heading small,.setting-block small,.preserve-note small{color:#8a948e!important;font-size:10px!important}.upscale-upload{height:190px;overflow:hidden;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:8px;position:relative;border:1px dashed #cdd6d0;border-radius:8px;background:#f8faf8;color:#78827c;text-align:center;cursor:pointer}.upscale-upload.dragging{border-color:#2f63cf;background:#f3f7ff}.upscale-upload input{position:absolute;opacity:0;pointer-events:none}.upscale-upload>i{font-size:28px;color:#6f91d4}.upscale-upload strong{font-size:13px}.upscale-upload small{max-width:260px;color:#929b95!important;font-size:10px!important}.upscale-upload img{width:100%;height:100%;display:block;object-fit:contain}.upscale-upload.filled span{position:absolute;right:10px;bottom:10px;border-radius:5px;padding:6px 9px;background:rgba(24,34,28,.78);color:#fff;font-size:10px}.source-meta{display:grid;grid-template-columns:1fr 24px 1fr;align-items:center;margin-top:10px;border-radius:7px;padding:10px;background:#f6f8f6}.source-meta>span{display:flex;flex-direction:column;gap:2px}.source-meta small{color:#8a948e!important;font-size:9px!important}.source-meta strong{font-size:12px}.source-meta>i{text-align:center;color:#a3aba6}.setting-block{margin-top:17px}.setting-block>label{display:block;margin-bottom:8px;color:#4f5d55;font-size:11px;font-weight:700}.option-grid{display:grid;gap:7px}.option-grid.two{grid-template-columns:repeat(2,1fr)}.option-grid.three{grid-template-columns:repeat(3,1fr)}.option-grid button{min-height:54px;display:flex;align-items:flex-start;justify-content:center;flex-direction:column;gap:3px;border:1px solid #dfe5e1;border-radius:7px;padding:8px 10px;background:#fff;color:#4b5850;text-align:left}.option-grid button:hover{border-color:#aebedb}.option-grid button.active{border-color:#3d72df;background:#f1f5ff;color:#2452ad;box-shadow:inset 0 0 0 1px #3d72df}.option-grid strong{font-size:11px}.preserve-note{display:flex;align-items:center;gap:9px;margin-top:16px;border-radius:7px;padding:10px 12px;background:#f5f7f5;color:#637068}.preserve-note>i{color:#54816a}.preserve-note span{display:flex;flex-direction:column;gap:2px}.preserve-note strong{font-size:10px}.upscale-generate{width:100%;min-height:52px;display:flex;align-items:center;justify-content:center;gap:10px;margin-top:15px;border:0;border-radius:7px;background:#2f63cf;color:#fff}.upscale-generate:disabled{background:#b8c4bb;cursor:not-allowed}.upscale-generate>i{font-size:17px}.upscale-generate span{display:flex;align-items:flex-start;flex-direction:column;gap:2px}.upscale-generate strong{font-size:12px}.upscale-generate small{color:rgba(255,255,255,.72)!important;font-size:9px!important}.upscale-progress{height:3px;overflow:hidden;margin-top:8px;border-radius:3px;background:#e5eae6}.upscale-progress i{height:100%;display:block;background:#3d72df;transition:width .3s}.upscale-error{display:flex;align-items:center;gap:7px;margin-top:10px;color:#c63f3f;font-size:10px}.upscale-preview{min-height:610px;display:flex;flex-direction:column;padding:18px}.upscale-preview>header,.upscale-history>header{display:flex;align-items:center;justify-content:space-between;gap:16px;padding-bottom:14px}.upscale-preview header>div:first-child,.upscale-history header>div{display:flex;flex-direction:column;gap:3px}.upscale-preview header strong,.upscale-history header strong{font-size:14px}.upscale-preview header small,.upscale-history header small{color:#8a948e!important;font-size:10px!important}.preview-actions{display:flex;gap:7px}.preview-actions button,.upscale-history header button{height:34px;border:1px solid #dfe5e1;border-radius:6px;padding:0 11px;background:#fff;color:#5c6861;font-size:10px}.preview-actions button.primary{border-color:#2f63cf;background:#2f63cf;color:#fff}.preview-empty{flex:1;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:8px;border-radius:8px;background:#f5f7f5;color:#8a948e}.preview-empty span{width:60px;height:60px;display:grid;place-items:center;border-radius:50%;background:#fff;color:#7a98d2;font-size:25px}.preview-empty strong{color:#5d6962;font-size:13px}.preview-empty small{font-size:10px!important}.comparison{min-height:0;flex:1;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}.comparison>button,.result-placeholder{min-width:0;overflow:hidden;position:relative;border:0;border-radius:8px;padding:0;background:#f1f3f1}.comparison>button img{width:100%;height:100%;display:block;object-fit:contain}.comparison>button span{position:absolute;left:10px;bottom:10px;border-radius:5px;padding:6px 9px;background:rgba(25,35,29,.76);color:#fff;font-size:9px}.comparison>button span.result-label{background:rgba(47,99,207,.9)}.result-placeholder{display:flex;align-items:center;justify-content:center;flex-direction:column;gap:8px;color:#87918b}.result-placeholder>i{font-size:28px;color:#7695d1}.result-placeholder strong{font-size:12px}.result-placeholder small{font-size:10px!important}.upscale-history{margin-top:16px;padding:18px}.history-grid{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:8px}.history-grid>button{min-width:0;overflow:hidden;display:flex;flex-direction:column;border:1px solid #e4e9e5;border-radius:7px;padding:0;background:#fff;color:#68736c;text-align:left}.history-grid img,.history-grid>button>span{width:100%;aspect-ratio:4/3;display:grid;place-items:center;object-fit:cover;background:#f2f4f2}.history-grid small{padding:8px;font-size:9px!important}.history-grid button:disabled{opacity:.65}@media(max-width:1100px){.upscale-grid{grid-template-columns:320px minmax(0,1fr)}.history-grid{grid-template-columns:repeat(4,1fr)}}@media(max-width:767px){.upscale-workspace{width:calc(100% - 26px);margin-top:18px;padding:0 0 36px}.upscale-grid{grid-template-columns:1fr}.upscale-preview{min-height:520px}.comparison{grid-template-columns:1fr;grid-template-rows:repeat(2,minmax(260px,1fr))}.history-grid{grid-template-columns:repeat(2,1fr)}}
.upscale-controls,
.upscale-preview,
.upscale-history {
  border-color: #e2e7e3;
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(37, 51, 43, .04);
}
.control-icon,
.preview-empty span {
  background: #edf4ff;
  color: #2563eb;
}
.upscale-upload:hover,
.upscale-upload.dragging {
  border-color: #2563eb;
  background: #f1f6ff;
}
.option-grid button.active {
  border-color: #7ca1e4;
  background: #f7f9fd;
  color: #1f55b5;
  box-shadow: 0 0 0 1px rgba(37, 99, 235, .08);
}
.upscale-generate,
.preview-actions button.primary {
  border-color: #2563eb;
  background: #2563eb;
}
.upscale-generate:hover:not(:disabled),
.preview-actions button.primary:hover {
  background: #1d4ed8;
}
.upscale-progress i {
  background: #2563eb;
}
.upscale-history {
  margin-top: 32px;
  border: 0;
  border-top: 1px solid #dfe5e0;
  border-radius: 0;
  padding: 26px 0 0;
  background: transparent;
  box-shadow: none;
}
.upscale-history > header {
  margin-bottom: 14px;
}
.history-empty {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 7px;
  border: 1px dashed #dce3de;
  border-radius: 8px;
  background: #f8faf8;
  color: #8a948e;
  text-align: center;
}
.history-empty > span {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #edf4ff;
  color: #2563eb;
  font-size: 18px;
}
.history-empty strong {
  color: #5f6b64;
  font-size: 11px;
}
.history-empty small {
  color: #929b95 !important;
  font-size: 9px !important;
}
.history-grid > .history-card {
  min-width: 0;
  overflow: hidden;
  border: 1px solid #e4e9e5;
  border-radius: 7px;
  background: #fff;
}
.history-card-preview {
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border: 0;
  padding: 0;
  background: #fff;
  color: #68736c;
  text-align: left;
}
.history-card-preview > img,
.history-card-preview > span {
  width: 100%;
  aspect-ratio: 4 / 3;
  display: grid;
  place-items: center;
  object-fit: cover;
  background: #f2f4f2;
}
.history-card-workflow { width: auto; margin: 0 8px 8px auto; }
</style>
